import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { analytics } from '../../../services/analytics';

interface PricingCard {
  badge: string | null;
  tier: string;
  price: string;
  priceSuffix: string | null;
  subtitle: string;
  footnote: string | null;
  features: string[];
  ctaLabel: string;
  ctaTo: string;
  featured: boolean;
}

const PRICING_CARDS: PricingCard[] = [
  {
    badge: null,
    tier: 'Free',
    price: 'Free',
    priceSuffix: null,
    subtitle: 'Forever',
    footnote: null,
    features: [
      'Sobriety tracking',
      'Morning check-in & nightly review',
      'All 12 steps, all fellowships',
      'Journal & gratitude entries',
      'Meeting finder',
      'Sponsor connection & sharing',
      'Homework system',
      'Push notification reminders',
      'Crisis resources',
    ],
    ctaLabel: 'Get Started Free',
    ctaTo: '/register',
    featured: false,
  },
  {
    badge: 'Most Popular',
    tier: 'Premium',
    price: '$2.99',
    priceSuffix: '/ mo',
    subtitle: 'or $10/year founding rate \u2736',
    footnote:
      '\u2736 Founding rate available during your 30-day trial. Standard annual rate is $14.99 after trial.',
    features: [
      'Everything in Free, plus:',
      'AI-powered check-in analysis',
      'AI nightly review insights',
      'Unlimited custom activities',
      'PDF export (activity log, step work)',
      'Unlimited journal history',
      'Streak grace period (1/month)',
      'Priority support',
    ],
    ctaLabel: 'Start 30-Day Free Trial',
    ctaTo: '/register',
    featured: true,
  },
];

const FAQ_ITEMS = [
  {
    question: 'What happens after 30 days?',
    answer:
      "If you haven't added a card, you continue with the Free tier. All your data is kept. Upgrade anytime.",
  },
  {
    question: 'Can I cancel anytime?',
    answer:
      'Yes. Cancel from Settings \u2192 Billing. No questions asked, no fees. Your account moves to the Free tier and all your data stays.',
  },
  {
    question: 'Is my data deleted if I cancel?',
    answer:
      'No. Your recovery data belongs to you. Canceling Premium keeps everything intact on the Free tier. You can export your data anytime from Settings \u2192 Privacy, and if you ever want to delete your account entirely that option is there too.',
  },
  {
    question: 'Can I export my data?',
    answer:
      'Yes. Go to Settings \u2192 Privacy \u2192 Export Data to download your profile and recovery history. Premium members can also export activity logs and step work as PDFs from within the app.',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

export function PricingSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          analytics.pricingSectionViewed();
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="pricing"
      ref={sectionRef}
      className="bg-navy-900 py-20 px-4"
    >
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <motion.div
          className="text-center mb-14"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Start free. Upgrade when you&apos;re ready.
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            No credit card required to get started. Full access to all features
            free for 30 days.
          </p>
        </motion.div>

        {/* Pricing cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16 items-start max-w-3xl mx-auto"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {PRICING_CARDS.map((card) => (
            <motion.div
              key={card.tier}
              variants={fadeUp}
              className={[
                'rounded-2xl bg-navy-800 p-8 flex flex-col gap-6',
                card.featured
                  ? 'border-2 border-teal-500 shadow-lg shadow-teal-900/30 md:-mt-4 md:mb-4'
                  : 'border border-navy-700',
              ].join(' ')}
            >
              {/* Badge */}
              {card.badge !== null ? (
                <span
                  className={[
                    'inline-block self-start rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider',
                    card.featured
                      ? 'bg-teal-500/20 text-teal-300'
                      : 'bg-slate-700 text-slate-300',
                  ].join(' ')}
                >
                  {card.badge}
                </span>
              ) : (
                /* Spacer to keep vertical rhythm when no badge */
                <div className="h-[26px]" aria-hidden="true" />
              )}

              {/* Tier name */}
              <div>
                <p className="text-slate-400 text-sm font-medium uppercase tracking-wider mb-1">
                  {card.tier}
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">
                    {card.price}
                  </span>
                  {card.priceSuffix !== null && (
                    <span className="text-slate-400 text-sm">
                      {card.priceSuffix}
                    </span>
                  )}
                </div>
                <p className="text-slate-400 text-sm mt-1">{card.subtitle}</p>
              </div>

              {/* Feature list */}
              <ul role="list" className="flex flex-col gap-3 flex-1">
                {card.features.map((feature) => {
                  const isHeader = feature.endsWith(':');
                  return (
                    <li key={feature} className="flex items-start gap-3">
                      {isHeader ? (
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider pt-1">
                          {feature}
                        </span>
                      ) : (
                        <>
                          <div className="mt-0.5 shrink-0 p-1 rounded-full bg-teal-500/15">
                            <CheckIcon className="w-3.5 h-3.5 text-teal-400" />
                          </div>
                          <span className="text-sm text-slate-300 leading-relaxed">
                            {feature}
                          </span>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>

              {/* Footnote */}
              {card.footnote !== null && (
                <p className="text-xs text-slate-500 leading-relaxed">
                  {card.footnote}
                </p>
              )}

              {/* CTA */}
              <Link
                to={card.ctaTo}
                onClick={() => analytics.landingCtaClicked(card.ctaLabel, `pricing_${card.tier.toLowerCase()}`)}
                className={[
                  'block text-center rounded-xl px-6 py-3 text-sm font-semibold transition-colors duration-200',
                  card.featured
                    ? 'bg-teal-500 hover:bg-teal-600 text-white'
                    : 'border border-white/20 text-white hover:bg-white/5',
                ].join(' ')}
              >
                {card.ctaLabel}
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* FAQ */}
        <motion.div
          className="max-w-2xl mx-auto"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
        >
          <motion.h3
            variants={fadeUp}
            className="text-center text-lg font-semibold text-white mb-8"
          >
            Common questions
          </motion.h3>

          <ul role="list" className="flex flex-col gap-6">
            {FAQ_ITEMS.map((item) => (
              <motion.li
                key={item.question}
                variants={fadeUp}
                className="rounded-xl bg-white/5 border border-white/10 p-6"
              >
                <p className="text-white font-semibold mb-2">{item.question}</p>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {item.answer}
                </p>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
